import React, { useCallback, useEffect, useLayoutEffect, useReducer, useRef, useState } from 'react';
import { About } from './components/Apps/About';
import { Archive } from './components/Apps/Archive';
import { Feed } from './components/Apps/Feed';
import { Lab } from './components/Apps/Lab';
import { Mov } from './components/Apps/Mov';
import { Notes } from './components/Apps/Notes';
import { Settings } from './components/Apps/Settings';
import { Works } from './components/Apps/Works';
import { DigitalClock } from './components/Clock';
import { Dock } from './components/Desktop';
import { CRTBackground } from './components/Effects';
import crtStyles from './components/Effects/CRT.module.css';
import { SheetType, Window } from './components/Layout';
import { useSettings } from './hooks/useSettings';
import {
    AppId,
    COMPACT_QUERY,
    defaultLayout,
    expandedRect,
    MIN_SIZES,
    Point,
    Size,
    tileLayout,
    windowReducer,
} from './hooks/windowManager';
import './App.css';

// DOM 순서는 고정하고 겹침은 z-index로만 바꾼다 (재배치되면 애니메이션과 초점이 끊긴다)
const APPS: { id: AppId; title: string; sheet: SheetType }[] = [
    { id: 'about', title: '소개', sheet: 'paper' },
    { id: 'feed', title: '책장', sheet: 'paper' },
    { id: 'clock', title: '시계', sheet: 'terminal' },
    { id: 'works', title: '작업', sheet: 'terminal' },
    { id: 'archive', title: '프로젝트', sheet: 'paper' },
    { id: 'lab', title: '실험실', sheet: 'signal' },
    { id: 'mov', title: '극장', sheet: 'terminal' },
    { id: 'notes', title: '메모', sheet: 'human' },
    { id: 'settings', title: '설정', sheet: 'paper' },
];

const SCREENSAVER_MS = 5000;
const DRIFT_MS = 1000;
const SETTLE_MS = 320;
const BOOT_KEY = 'dkdk_booted';

const hasBooted = () => {
    try {
        return sessionStorage.getItem(BOOT_KEY) === '1';
    } catch (_) {
        return true;
    }
};

/**
 * dkdk: CRT 위에 패널을 늘어놓은 개인 허브.
 * 소개, 작업, 시계와 각 사이트로 이어지고, 패널은 옮기고 크기를 바꾸고 닫을 수 있다.
 */
function App() {
    const { settings, updateSettings } = useSettings();
    const [booting] = useState(() => !hasBooted());
    const desktopRef = useRef<HTMLDivElement>(null);
    const [bounds, setBounds] = useState<Size | null>(null);
    const [compact, setCompact] = useState(false);
    const [windows, dispatch] = useReducer(windowReducer, { stack: [], rects: {}, moved: [], resized: [] });
    const windowsRef = useRef(windows);
    windowsRef.current = windows;
    const [drifting, setDrifting] = useState(false);
    const [settling, setSettling] = useState(false);
    const settleTimerRef = useRef<number | undefined>(undefined);
    const [expanded, setExpanded] = useState<AppId | null>(null);
    const [expanding, setExpanding] = useState<AppId | null>(null);
    const expandTimerRef = useRef<number | undefined>(undefined);

    const focusedId = windows.stack[windows.stack.length - 1];

    // 패널 영역을 재고, 처음 잰 순간 기본 배치를 잡는다. 이후엔 크기만 다시 맞춘다
    useLayoutEffect(() => {
        const element = desktopRef.current!;
        let measured = false;

        const measure = () => {
            const next = { width: element.clientWidth, height: element.clientHeight };
            const narrow = window.matchMedia(COMPACT_QUERY).matches;
            const layout = defaultLayout(next, narrow);
            dispatch(measured ? { type: 'resize', rects: layout.rects } : { type: 'layout', state: layout });
            measured = true;
            setCompact(narrow);
            setBounds(next);
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        try {
            sessionStorage.setItem(BOOT_KEY, '1');
        } catch (_) { }
    }, []);

    const openApp = useCallback((id: AppId) => {
        dispatch({ type: 'open', id, exclusive: compact });
    }, [compact]);

    // 펼친 크기는 저장하지 않는다. 접으면 사람이 놓아둔 자리와 크기로 돌아간다
    const toggleExpanded = useCallback((id: AppId) => {
        setExpanded((current) => (current === id ? null : id));
        setExpanding(id);
        window.clearTimeout(expandTimerRef.current);
        expandTimerRef.current = window.setTimeout(() => setExpanding(null), SETTLE_MS);
    }, []);

    // 열린 패널을 그리드에 맞춘다. 설정 패널은 이 버튼이 있는 자리라 움직이지 않는다
    const arrangeWindows = () => {
        if (!bounds) return;
        const ids = windowsRef.current.stack.filter((id) => id !== 'settings');

        setSettling(true);
        setExpanded(null);
        dispatch({ type: 'tile', rects: tileLayout(ids, bounds) });
        window.clearTimeout(settleTimerRef.current);
        settleTimerRef.current = window.setTimeout(() => setSettling(false), SETTLE_MS);
    };

    // 화면보호기: 번인을 막으려 열린 패널이 모두 자리를 옮기고, 끄면 원래 자리로 돌아간다
    const boundsWidth = bounds?.width;
    const boundsHeight = bounds?.height;
    useEffect(() => {
        if (!settings.screensaver || !boundsWidth || !boundsHeight || compact) return;

        const home = windowsRef.current.rects;
        const drift = () => {
            const { stack, rects } = windowsRef.current;
            const positions: Partial<Record<AppId, Point>> = {};

            stack.forEach((id) => {
                const rect = rects[id];
                if (!rect) return;
                positions[id] = {
                    x: Math.random() * Math.max(0, boundsWidth - rect.width),
                    y: Math.random() * Math.max(0, boundsHeight - rect.height),
                };
            });
            dispatch({ type: 'drift', positions });
        };

        setDrifting(true);
        drift();
        const interval = window.setInterval(drift, SCREENSAVER_MS);

        return () => {
            window.clearInterval(interval);
            dispatch({ type: 'drift', positions: home });
            window.setTimeout(() => setDrifting(false), DRIFT_MS);
        };
    }, [settings.screensaver, boundsWidth, boundsHeight, compact]);

    const renderApp = (id: AppId) => {
        switch (id) {
            case 'about':
                return <About expanded={compact || expanded === id} compact={compact} onToggle={() => toggleExpanded(id)} />;
            case 'feed':
                return <Feed />;
            case 'clock':
                return <DigitalClock />;
            case 'works':
                return <Works />;
            case 'archive':
                return <Archive expanded={compact || expanded === id} compact={compact} onToggle={() => toggleExpanded(id)} />;
            case 'lab':
                return <Lab />;
            case 'mov':
                return <Mov />;
            case 'notes':
                return <Notes />;
            case 'settings':
                return <Settings settings={settings} onChange={updateSettings} onArrangeWindows={arrangeWindows} />;
        }
    };

    return (
        <main
            className={`screen ${booting ? 'booting' : ''}`}
            style={{
                '--scan-strength': settings.crt ? settings.scanlines : 0,
                '--curve-strength': settings.curvature,
                '--noise-strength': settings.noise,
            } as React.CSSProperties}
        >
            <h1 className="visuallyHidden">dkdk</h1>
            <CRTBackground
                enabled={settings.crt}
                scanlines={settings.scanlines}
                curvature={settings.curvature}
                noise={settings.noise}
            />

            <div ref={desktopRef} className="desktop">
                {bounds && APPS.filter(({ id }) => windows.stack.includes(id)).map(({ id, title, sheet }) => (
                    <Window
                        key={id}
                        title={title}
                        sheet={sheet}
                        rect={expanded === id ? expandedRect(bounds) : windows.rects[id]}
                        minSize={MIN_SIZES[id]}
                        bounds={bounds}
                        layer={windows.stack.indexOf(id) + 1}
                        focused={id === focusedId}
                        compact={compact}
                        drifting={drifting}
                        settling={settling || expanding === id}
                        expanded={expanded === id}
                        onCollapse={() => toggleExpanded(id)}
                        onFocus={() => dispatch({ type: 'focus', id })}
                        onClose={() => {
                            if (expanded === id) setExpanded(null);
                            dispatch({ type: 'close', id });
                        }}
                        onMove={(position) => dispatch({ type: 'move', id, position })}
                        onResize={(rect) => dispatch({ type: 'place', id, rect })}
                    >
                        {renderApp(id)}
                    </Window>
                ))}
            </div>

            <Dock openApps={windows.stack} compact={compact} onOpen={openApp} />

            {settings.crt && (
                <div className={crtStyles.glass} aria-hidden="true">
                    <div className={crtStyles.refreshBand} />
                </div>
            )}
        </main>
    );
}

export default App;
